import type {
    EshopProductApiResponses,
    EshopProductPart,
    MappedEshopAvailability,
    MappedEshopBody,
    MappedEshopBrand,
    MappedEshopDetail,
    MappedEshopProduct,
    ProductAvailabilityEshopApi,
    ProductDetailEshopApi,
    ProductPriceEshopApi,
    ProductSeasonEshopApi,
    ProductStickerApi,
    ProductUnitEshopApi,
} from '#/global/service/ProductApiTypes'

import { Config } from '#/global/helper/Config'

const buildImageUrl = (folder: 'product-eshop' | 'product-stick', image: number) => {
    const cdn = Config.getBaseCdn().replace(/\/$/, '')
    return `${cdn}/${Config.getShopId()}/img/${folder}/${image}.webp`
}

const mapDetail = (detail: ProductDetailEshopApi): MappedEshopDetail => ({
    title: detail.title,
    slug: detail.slug ?? '',
    categoryId: detail.categoryId ?? 0,
    image: buildImageUrl('product-eshop', detail.image),
    descriptionShort: detail.descriptionShort,
    isFeatured: detail.isFeature,
    isRecommended: detail.isRecommended,
    isRecent: detail.isRecent,
    seasonPointsText: detail.seasonPointsText,
    hasGift: detail.hasGift,
    hasPpl: detail.hasPpl,
    hasVariant: detail.hasVariant,
})

const mapBrand = (detail: ProductDetailEshopApi): MappedEshopBrand | null => detail.brandId === 0
    ? null
    : {
            id: detail.brandId,
            title: detail.brandTitle,
            slug: detail.brandSlug,
        }

const mapPrice = (price: ProductPriceEshopApi) => ({
    ...price,
    discountPercent: price.discountPercentOrigin,
})

const mapUnit = (unit: ProductUnitEshopApi) => unit

const mapAvailability = (availability: ProductAvailabilityEshopApi): MappedEshopAvailability => ({
    availabilityCommonText: availability.availabilityCommonText,
    availabilityCommonCode: availability.availabilityCommonCode,
    availabilityBranchText: availability.availabilityBranchText,
    availabilityBranchCode: availability.availabilityBranchCode,
    isWhileStockLast: availability.isWhileStockLast,
    isNotForSale: availability.isNotForSale,
})

const mapSeason = (season: ProductSeasonEshopApi) => season

const mapSticker = (sticker: ProductStickerApi) => ({
    id: sticker.id,
    image: buildImageUrl('product-stick', sticker.image),
    isVisibleInCategory: sticker.isVisibleInCategory,
    ...(sticker.tooltip == null ? {} : { tooltip: sticker.tooltip }),
})

const mapPart = (
    product: MappedEshopBody,
    part: EshopProductPart,
    index: number,
    responses: EshopProductApiResponses,
    detail: ProductDetailEshopApi | null,
) => {
    switch (part) {
        case 'availability':
            product.availability = responses.availability?.[index] == null
                ? null
                : mapAvailability(responses.availability[index])
            break
        case 'brand':
            product.brand = detail === null ? null : mapBrand(detail)
            break
        case 'detail':
            product.detail = detail === null ? null : mapDetail(detail)
            break
        case 'price':
            product.price = responses.price?.[index] == null ? null : mapPrice(responses.price[index])
            break
        case 'season':
            product.season = responses.season?.[index] == null ? null : mapSeason(responses.season[index])
            break
        case 'sticker':
            product.sticker = (responses.sticker?.[index]?.stickers ?? []).map(sticker => mapSticker(sticker))
            break
        case 'unit':
            product.unit = responses.unit?.[index] == null ? null : mapUnit(responses.unit[index])
            break
    }
}

/** Map positional e-shop API responses into the legacy UI product shape. */
export function mapEshopProducts<T extends EshopProductPart>(
    codes: readonly string[],
    parts: readonly T[],
    responses: EshopProductApiResponses,
): Array<MappedEshopProduct<T> | null>
export function mapEshopProducts(
    codes: readonly string[],
    parts: readonly EshopProductPart[],
    responses: EshopProductApiResponses,
): Array<MappedEshopBody | null> {
    return codes.map((code, index) => {
        const detail = responses.detail?.[index] ?? null
        if (responses.detail !== undefined && detail === null) {
            return null
        }

        const product: MappedEshopBody = { code, type: 'eshop' }
        for (const part of parts) {
            mapPart(product, part, index, responses, detail)
        }

        return product
    })
}
